import { Injectable } from '@angular/core';
import { MarkerClassification } from '../models/marker-classification';
import { ServiceProviderMarkerIcon } from "../interfaces/ServiceProviderMarkerIcon";

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  /**
   * Maps and visualizes multiple groups of locations on a Google Map using a 2D array approach.
   * This function supports drawing separate routes for each group and adds markers to the map.
   *
   * @param {Array<any>} wayPoints - Array to store waypoints for the route. Reset for each group.
   * @param {any[]} locationsGroup - 2D array where each sub-array contains a group of location objects to be mapped.
   * @param {any} map - Google Maps instance where the locations and routes will be rendered.
   * @param {any} MarkerInfo - Metadata about the markers (e.g., keys, labels, icons).
   * @param {any[]} ledger - Array to store classification data for jobs, contractors, or other entities.
   * @param {string[]} colorsArray - Array of color strings, each corresponding to a route for a group in `locationsGroup`.
   * @param {string | null} custom1DColour - This is not used in mapBy2DArray, but to fit with the structure of MapBy1DArray
   * @param {boolean} showCount - Flag indicating whether to display a count label on markers.
   * @param {string} htmlText - Accumulated HTML text for marker descriptions or info windows.
   * @param {string} link - URL for job or contractor redirection, dynamically updated.
   * @param {string} linkDescription - Description of the redirection link, dynamically updated.
   * @param {boolean} hasJobRedirect - Indicates whether job redirection is enabled.
   * @param {boolean} hasContractorRedirect - Indicates whether contractor redirection is enabled.
   * @param {boolean} showRoutes - Flag indicating whether to draw routes between locations for each group on the map.
   *
   * @returns {Object} - Returns an object containing:
   *   - `directionsRenderer`: Array of Google Maps DirectionsRenderer instances, one for each group.
   *   - `directionsService`: Array of Google Maps DirectionsService instances, one for each group.
   *   - `ledger`: Updated ledger array with classification data for jobs or contractors.
   */
  MapBy2DArray(
    wayPoints: Array<any>,
    locationsGroup: any[],
    map: any,
    MarkerInfo: any,
    ledger: any[],
    colorsArray: string[],
    custom1DColour: string | null,
    showCount: boolean,
    htmlText: string,
    link: string,
    linkDescription: string,
    hasJobRedirect: boolean,
    hasContractorRedirect: boolean,
    showRoutes: boolean,
  ): { directionsRenderer: any[]; directionsService: any[]; ledger: any[] } {
    let directionsService: Array<google.maps.DirectionsService> = new Array(locationsGroup.length);
    let directionsRenderer: Array<google.maps.DirectionsRenderer> = new Array(locationsGroup.length);
    let count = 0;

    for (let j = 0; j < locationsGroup.length; j++) {
      wayPoints = [];
      directionsService[j] = new google.maps.DirectionsService();
      directionsRenderer[j] = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: colorsArray[j],
        },
      });

      let originPoint: any = {};
      let destinationPoint: any = {};

      for (let i = 0; i < locationsGroup[j].length; i++) {
        if (locationsGroup[j][i].Type != 'Contractors') {
          count++;
        }

        ledger = this.DrawMaker(
          locationsGroup[j][i],
          new google.maps.LatLng(locationsGroup[j][i].lat, locationsGroup[j][i].lng),
          count,
          map,
          locationsGroup[j][i].Type === 'Contractors',
          MarkerInfo,
          ledger,
          showCount,
          htmlText,
          link,
          linkDescription,
          hasJobRedirect,
          hasContractorRedirect,
        );

        if (i == 0) {
          originPoint = new google.maps.LatLng(locationsGroup[j][i].lat, locationsGroup[j][i].lng);
          destinationPoint = new google.maps.LatLng(locationsGroup[j][i].lat, locationsGroup[j][i].lng);
          continue;
        }

        wayPoints.push({
          location: new google.maps.LatLng(locationsGroup[j][i].lat, locationsGroup[j][i].lng),
          stopover: true,
        });
      }

      if (showRoutes) {
        directionsRenderer[j].setMap(map);

        let requestV: google.maps.DirectionsRequest = {
          origin: originPoint,
          waypoints: wayPoints,
          destination: destinationPoint,
          avoidFerries: true,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
          provideRouteAlternatives: true,
          avoidHighways: false,
          avoidTolls: false,
        };

        directionsService[j].route(requestV, (result: any, status: string) => {
          if (status == 'OK') {
            directionsRenderer[j].setDirections(result);
          }
        });
      }
    }

    return { directionsRenderer, directionsService, ledger };
  }

  /**
   * Maps and visualizes a group of locations on a Google Map using a 1D array approach.
   * This includes adding markers for each location, managing waypoints for routes, and rendering directions on the map.
   *
   * @param {Array<any>} wayPoints - Array to store waypoints for the route.
   * @param {any[]} locationsGroup - Array of location objects to be mapped.
   * @param {any} map - Google Maps instance where the locations and routes will be rendered.
   * @param {any} MarkerInfo - Metadata about the markers (e.g., keys, labels, icons).
   * @param {any[]} ledger - Array to store classification data for jobs, contractors, or other entities.
   * @param {string[]} colorsArray - Array of color strings used for styling routes or markers.
   * @param {string | null} custom1DColour - Custom color for the route polyline. If null, defaults to the second color in `colorsArray`.
   * @param {boolean} showCount - Flag indicating whether to display a count label on markers.
   * @param {string} htmlText - Accumulated HTML text for marker descriptions or info windows.
   * @param {string} link - URL for job or contractor redirection, dynamically updated.
   * @param {string} linkDescription - Description of the redirection link, dynamically updated.
   * @param {boolean} hasJobRedirect - Indicates whether job redirection is enabled.
   * @param {boolean} hasContractorRedirect - Indicates whether contractor redirection is enabled.
   * @param {boolean} showRoutes - Flag indicating whether to draw routes between locations on the map.
   *
   * @returns {Object} - Returns an object containing:
   *   - `directionsRenderer`: Google Maps DirectionsRenderer instance for visualizing routes.
   *   - `directionsService`: Google Maps DirectionsService instance for calculating routes.
   *   - `ledger`: Updated ledger array with classification data for jobs or contractors.
   */
  MapBy1DArray(
    wayPoints: Array<any>,
    locationsGroup: any[],
    map: any,
    MarkerInfo: any,
    ledger: any[],
    colorsArray: string[],
    custom1DColour: string | null,
    showCount: boolean,
    htmlText: string,
    link: string,
    linkDescription: string,
    hasJobRedirect: boolean,
    hasContractorRedirect: boolean,
    showRoutes: boolean,
  ): { directionsRenderer: any; directionsService: any; ledger: any[] } {
    let directionsService = new google.maps.DirectionsService();
    let directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: custom1DColour ?? colorsArray[1],
      },
    });
    let count = 0;
    let originPoint: any = {};
    let destinationPoint: any = {};

    for (let i = 0; i < locationsGroup.length; i++) {
      if (locationsGroup[i].Type != 'Contractors') {
        count++;
      }

      ledger = this.DrawMaker(
        locationsGroup[i],
        new google.maps.LatLng(locationsGroup[i].lat, locationsGroup[i].lng),
        count,
        map,
        locationsGroup[i].Type === 'Contractors',
        MarkerInfo,
        ledger,
        showCount,
        htmlText,
        link,
        linkDescription,
        hasJobRedirect,
        hasContractorRedirect,
      );

      if (i == 0) {
        originPoint = new google.maps.LatLng(locationsGroup[i].lat, locationsGroup[i].lng);
        destinationPoint = new google.maps.LatLng(locationsGroup[i].lat, locationsGroup[i].lng);
        continue;
      }

      wayPoints.push({
        location: new google.maps.LatLng(locationsGroup[i].lat, locationsGroup[i].lng),
        stopover: true,
      });
    }

    if (showRoutes) {
      directionsRenderer.setMap(map);

      let requestV: google.maps.DirectionsRequest = {
        origin: originPoint,
        waypoints: wayPoints,
        destination: destinationPoint,
        avoidFerries: true,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
        provideRouteAlternatives: true,
        avoidHighways: false,
        avoidTolls: false,
      };

      directionsService.route(requestV, (result: any, status: string) => {
        if (status == 'OK') {
          directionsRenderer.setDirections(result);
        }
      });
    }

    return { directionsRenderer, directionsService, ledger };
  }

  /**
   * Draws a marker on the Google Maps instance with optional customization for jobs or contractors.
   * Updates the ledger with classification details and assigns markers based on the provided configuration.
   *
   * @param {any} location - The data object containing details about the marker (e.g., `Type`, `JobType`, `JobStatus`).
   * @param {google.maps.LatLng} position - The geographical coordinates (latitude and longitude) for the marker.
   * @param {number} count - A counter for marker labeling (e.g., the number of markers at a location).
   * @param {any} map - The Google Maps instance where the marker will be drawn.
   * @param {boolean} [isContractorHome=false] - Whether the marker represents a contractor's home. Defaults to `false`.
   * @param {any} markerInfo - An array of metadata for marker details (e.g., keys, labels, link details).
   * @param {any} ledger - The current ledger to store classification data for jobs or contractors.
   * @param {boolean} showCount - Whether to display a count or a "CH" label on the marker.
   * @param {string} htmlText - The accumulated HTML content describing the marker details.
   * @param {string} link - The URL for job or contractor redirection, updated dynamically.
   * @param {string} linkDescription - A description of the redirection link, updated dynamically.
   * @param {boolean} hasJobRedirect - A flag indicating whether job redirection is enabled.
   * @param {boolean} hasContractorRedirect - A flag indicating whether contractor redirection is enabled.
   *
   * @returns {any[]} - An updated ledger array with additional classification entries based on the marker type.
   */
  DrawMaker(
    location: any,
    position: google.maps.LatLng,
    count: number,
    map: any,
    isContractorHome: boolean = false,
    markerInfo: any,
    ledger: any,
    showCount: boolean,
    htmlText: string,
    link: string,
    linkDescription: string,
    hasJobRedirect: boolean,
    hasContractorRedirect: boolean,
  ) {
    let iconName = '';
    let updatedLedger = [...ledger];

    if (!showCount) {
      if (markerInfo && markerInfo.length > 0) {
        for (let i = 0; i < markerInfo.length; i++) {
          if (location['Type'] === 'Jobs') {
            const jobInfo = this.JobsInformation(location, i, markerInfo, htmlText, link, linkDescription, hasJobRedirect);
            htmlText = jobInfo.htmlText;
            link = jobInfo.link;
            linkDescription = jobInfo.linkDescription;
          } else {
            const contractorInfo = this.ContractorsInformation(location, i, markerInfo, htmlText, link, linkDescription, hasContractorRedirect);
            htmlText = contractorInfo.htmlText;
            link = contractorInfo.link;
            linkDescription = contractorInfo.linkDescription;
          }
        }
      }

      iconName = this.GetMarkerIconName(location);
      updatedLedger = this.UpdateLedger(location, ledger);
    }

    let marker = new google.maps.Marker({
      position: position,
      icon: iconName,
      map: map,
    });

    if (showCount) {
      marker = new google.maps.Marker({
        position: position,
        label: {
          text: isContractorHome ? 'CH' : `${count}`,
          className: 'markerText',
          color: '#FFFFFF',
        },
        map: map,
      });
    }

    this.DrawInfoWindow(marker, location, count, map, link, linkDescription);

    return updatedLedger;
  }

  /**
   * Processes job-specific information and returns updated HTML text, link, and link description.
   *
   * @param {any} location - The data object containing location details such as `Type` and specific marker information keys.
   * @param {number} i - The index of the marker in the markerInfo array being processed.
   * @param {any} markerInfo - An array of marker metadata containing keys, labels, and optional wrappers for display logic.
   * @param {string} htmlText - The current HTML string that accumulates location-related content.
   * @param {string} link - The URL for job redirection, updated based on the current marker.
   * @param {string} linkDescription - A description for the link, updated based on the current marker's label.
   * @param {boolean} hasJobRedirect - A flag indicating whether job redirection is enabled.
   *
   * @returns {{ htmlText: string; link: string; linkDescription: string }}
   * An object containing the updated `htmlText`, `link`, and `linkDescription`.
   */
  JobsInformation(
    location: any,
    i: number,
    markerInfo: any,
    htmlText: string,
    link: string,
    linkDescription: string,
    hasJobRedirect: boolean,
  ): { htmlText: string; link: string; linkDescription: string } {
    let wrapperText = '';

    if (location[markerInfo[i].Key] && markerInfo[i].Wrapper != null && markerInfo[i].isLink == null) {
      wrapperText = markerInfo[i].Wrapper(location[markerInfo[i].Key], i);
    }

    if (hasJobRedirect) {
      if (markerInfo[i].isLink == null) {
        if (markerInfo[i].Key !== 'Type' && markerInfo[i].Type === 'Jobs') {
          htmlText += `<p>${markerInfo[i].Label} : ${markerInfo[i].Wrapper != null ? wrapperText : location[markerInfo[i].Key]}</p>`;
        }
      } else if (markerInfo[i].isLink) {
        if (markerInfo[i].Type === location['Type']) {
          link =
            markerInfo[i].link != null && markerInfo[i].isLink === true && markerInfo[i].Wrapper != null
              ? markerInfo[i].link + markerInfo[i].Wrapper(location[markerInfo[i].Key], i)
              : markerInfo[i].link + location[markerInfo[i].Key];
          linkDescription = markerInfo[i].link != null ? markerInfo[i].Label : '';
        }
      }
    }

    return { htmlText, link, linkDescription };
  }

  /**
   * Processes contractor-specific information and returns updated HTML text, link, and link description.
   *
   * @param {any} location - The data object containing location details such as `Type` and specific marker information keys.
   * @param {number} i - The index of the marker in the markerInfo array being processed.
   * @param {any} markerInfo - An array of marker metadata containing keys, labels, and optional wrappers for display logic.
   * @param {string} htmlText - The current HTML string that accumulates location-related content.
   * @param {string} link - The URL for contractor redirection, updated based on the current marker.
   * @param {string} linkDescription - A description for the link, updated based on the current marker's label.
   * @param {boolean} hasContractorRedirect - A flag indicating whether contractor redirection is enabled.
   *
   * @returns {{ htmlText: string; link: string; linkDescription: string }}
   * An object containing the updated `htmlText`, `link`, and `linkDescription`.
   */
  ContractorsInformation(
    location: any,
    i: number,
    markerInfo: any,
    htmlText: string,
    link: string,
    linkDescription: string,
    hasContractorRedirect: boolean,
  ): { htmlText: string; link: string; linkDescription: string } {
    let wrapperText = '';

    if (location[markerInfo[i].Key] && markerInfo[i].Wrapper != null && markerInfo[i].isLink == null) {
      wrapperText = markerInfo[i].Wrapper(location[markerInfo[i].Key], i);
    }

    if (hasContractorRedirect) {
      if (markerInfo[i].isLink == null) {
        if (markerInfo[i].Key !== 'Type' && markerInfo[i].Type === 'Contractors') {
          htmlText += `<p>${markerInfo[i].Label} : ${markerInfo[i].Wrapper != null ? wrapperText : location[markerInfo[i].Key]}</p>`;
        }
      } else if (markerInfo[i].isLink) {
        if (markerInfo[i].Type === location['Type']) {
          link =
            markerInfo[i].link != null && markerInfo[i].isLink === true && markerInfo[i].Wrapper != null
              ? markerInfo[i].link + markerInfo[i].Wrapper(location[markerInfo[i].Key], i)
              : markerInfo[i].link + location[markerInfo[i].Key];
          linkDescription = markerInfo[i].link != null ? markerInfo[i].Label : '';
        }
      }
    }

    return { htmlText, link, linkDescription };
  }

  /**
   * Draws an InfoWindow on the provided Google Maps marker.
   *
   * @param {google.maps.Marker} marker - The Google Maps marker where the InfoWindow will be displayed.
   * @param {any} location - The data object containing location details like `lat`, `lng`, `JobNumber`, and `address`.
   * @param {number} count - A numerical identifier for the marker or location (used for internal logic, if necessary).
   * @param {any} map - The Google Maps object to which the marker and InfoWindow are attached.
   * @param {string} link - A URL to be displayed as a clickable link within the InfoWindow.
   * @param {string} description - A text description for the clickable link.
   */
  DrawInfoWindow(marker: google.maps.Marker, location: any, count: number, map: any, link: string, description: string) {
    let infowindow = new google.maps.InfoWindow();
    google.maps.event.addListener(
      marker,
      'click',
      (() => {
        return () => {
          infowindow.setOptions({
            content: `
                <style>
                    .gm-style-iw, .gm-style-iw-d{ height: fit-content !important; max-height: 250px !important; }
                </style>
                <div id="content" style="overflow-y: scroll !important; text-align: center">
                    <div>
                    ${location.JobNumber ?? location.ContractorRef}
                    <p>Latitude: ${location.lat}</p>
                    <p>Longitude: ${location.lng}</p>
                    ${location.address != null ? `<p>Address: ${location.address}</p>` : ''}
                    </div>

                    <div>
                    <a href=${link}>${description}</a>
                    </div>
                </div>
                
                `,
            maxWidth: 300,
            minWidth: 300,
          });
          infowindow.open(map, marker);
        };
      })(),
    );
    map.addListener('click', function () {
      infowindow.close();
    });
  }

  /**
   * Retrieves the marker icon name for a given location based on its classification.
   *
   * @param {any} location - The location object containing details such as `Type`, `JobType`, `JobStatus`, and `JobPriority`.
   * @returns {string} - The name of the marker icon.
   */
  GetMarkerIconName(location: any): string {
    const markerClassification = new MarkerClassification(location?.Type || '');
    const classification = markerClassification.getClassification();
  
    switch (classification) {
      case 'Jobs':
        markerClassification.setJobIconClassificationType(location.JobType?.Name || '');
        return markerClassification.getJobIcon(
          location.JobStatus?.Name || '',
          location.JobPriority?.Name || ''
        );
  
      case 'Contractors':
        return markerClassification.getContractorIcon();
  
      default:
        return markerClassification.getMikrotikIcon();
    }
  }

  /**
   * Updates the ledger with an entry corresponding to the provided location's classification.
   *
   * @param {any} location - The location object containing details such as `Type`, `JobType`, `JobStatus`, and `JobPriority`.
   * @param {any[]} ledger - The existing ledger array to be updated with new entries.
   * @returns {any[]} - The updated ledger array with the new entry added if it doesn't already exist.
   */
  UpdateLedger(location: any, ledger: any[]): any[] {
    const markerClassification = new MarkerClassification(location?.Type || '');
    const classification = markerClassification.getClassification();
    const updatedLedger = [...ledger];
    let ledgerEntry: { Name: any; icon?: any; };
  
    switch (classification) {
      case 'Jobs':
        markerClassification.setJobIconClassificationType(location.JobType?.Name || '');
        ledgerEntry = markerClassification.getJobsLedger(
          location.JobType?.Name || '',
          location.JobStatus?.Name || '',
          location.JobPriority?.Name || ''
        );
        break;
  
      case 'Contractors':
        ledgerEntry = markerClassification.getContractorLedger();
        break;
  
      default:
        ledgerEntry = markerClassification.getMikrotikLedger();
        break;
    }
  
    if (ledgerEntry && !updatedLedger.some((data: any) => data.Name === ledgerEntry.Name)) {
      updatedLedger.push(ledgerEntry);
    }
  
    return updatedLedger;
  }  
  
}
